"use client";

import { useState, useTransition, type ReactNode } from "react";
import { useAction } from "convex/react";
import {
  Bell,
  CheckCircle2,
  LineChart,
  Loader2,
  type LucideIcon,
} from "@/components/icons";
import { api } from "@convex/_generated/api";
import type { PriceAlertKind } from "@convex/constants/priceAlerts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { handleError } from "@/lib/handle-error";

interface AlertSignupProps {
  kind: PriceAlertKind;
  productTypeSlugs: string[];
  subject: string;
  stateSlug?: string;
  defaultEmail?: string;
  className?: string;
}

interface AlertCopy {
  icon: LucideIcon;
  label: string;
  description?: string;
  buttonLabel: string;
  confirmation: string;
  collapsed?: { prompt: string; buttonLabel: string };
}

function copyFor(
  kind: PriceAlertKind,
  subject: string,
  email: string,
): AlertCopy {
  if (kind === "weekly_price_report") {
    return {
      icon: LineChart,
      label: `Weekly price report: ${subject}`,
      buttonLabel: "Subscribe",
      confirmation: `Subscribed. ${email} gets the first report on Monday.`,
    };
  }
  return {
    icon: Bell,
    label: `Email me new ${subject}`,
    buttonLabel: "Subscribe",
    confirmation: `Subscribed. ${email} gets new ${subject} once a day.`,
    collapsed: {
      prompt: `Get an email when new ${subject} are listed.`,
      buttonLabel: "Set up an alert",
    },
  };
}

function useAlertSignup(input: {
  kind: PriceAlertKind;
  productTypeSlugs: string[];
  subject: string;
  stateSlug?: string;
}) {
  const [subscribed, setSubscribed] = useState<{
    email: string;
    subject: string;
    pending: boolean;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();
  const subscribeToAlerts = useAction(
    api.priceAlerts.actions.subscribeToAlerts,
  );

  function submit(email: string) {
    const submittedSubject = input.subject;
    setError(null);
    startTransition(async () => {
      try {
        const result = await subscribeToAlerts({
          email,
          productTypeSlugs: input.productTypeSlugs,
          stateCodes: input.stateSlug ? [input.stateSlug] : undefined,
          kind: input.kind,
        });
        if (result.success) {
          setSubscribed({
            email,
            subject: submittedSubject,
            pending: result.status !== "active",
          });
        }
        else setError(result.message || "Couldn't subscribe. Try again.");
      } catch (failure) {
        setError(
          handleError(failure, { display: "silent" }).message ??
            "Something went wrong. Try again in a moment.",
        );
      }
    });
  }

  return { subscribed, error, isPending, submit };
}

function Subscribed({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      role="status"
      className={cn(
        "flex items-center gap-2 rounded-md border border-[var(--success)]/40 bg-[var(--success)]/10 px-4 py-3 text-sm text-success-strong",
        className,
      )}
    >
      <CheckCircle2 className="size-4 shrink-0" aria-hidden />
      <span>{children}</span>
    </div>
  );
}

function Collapsed({
  copy,
  className,
  onExpand,
}: {
  copy: AlertCopy & { collapsed: NonNullable<AlertCopy["collapsed"]> };
  className?: string;
  onExpand: () => void;
}) {
  const Icon = copy.icon;
  return (
    <div
      className={cn(
        "flex flex-col gap-2 rounded-md border border-border bg-muted/40 px-4 py-3 sm:flex-row sm:items-center sm:justify-between",
        className,
      )}
    >
      <div className="flex items-start gap-2 sm:items-center">
        <Icon
          className="mt-0.5 size-4 shrink-0 text-muted-foreground sm:mt-0"
          aria-hidden
        />
        <p className="text-sm text-foreground">{copy.collapsed.prompt}</p>
      </div>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={onExpand}
        className="shrink-0"
      >
        {copy.collapsed.buttonLabel}
      </Button>
    </div>
  );
}

function EmailRow({
  id,
  email,
  onChange,
  isPending,
  buttonLabel,
}: {
  id: string;
  email: string;
  onChange: (email: string) => void;
  isPending: boolean;
  buttonLabel: string;
}) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <Input
          id={id}
          type="email"
          required
          placeholder="you@company.com"
          value={email}
          onChange={(event) => onChange(event.target.value)}
          autoComplete="email"
          disabled={isPending}
        />
      </div>
      <Button variant="primary" type="submit" size="sm" disabled={isPending}>
        {isPending && <Loader2 className="mr-1 size-3 animate-spin" />}
        {buttonLabel}
      </Button>
    </div>
  );
}

export function AlertSignup({
  kind,
  productTypeSlugs,
  subject,
  stateSlug,
  defaultEmail = "",
  className,
}: AlertSignupProps) {
  const [email, setEmail] = useState(defaultEmail);
  const copy = copyFor(kind, subject, email);
  const [expanded, setExpanded] = useState(copy.collapsed === undefined);
  const signup = useAlertSignup({ kind, productTypeSlugs, subject, stateSlug });
  const inputId = `alert-signup-${kind}`;

  if (signup.subscribed) {
    return (
      <Subscribed className={className}>
        {signup.subscribed.pending
          ? `Check ${signup.subscribed.email} to confirm.`
          : copyFor(kind, signup.subscribed.subject, signup.subscribed.email)
              .confirmation}
      </Subscribed>
    );
  }
  if (!expanded && copy.collapsed) {
    return (
      <Collapsed
        copy={{ ...copy, collapsed: copy.collapsed }}
        className={className}
        onExpand={() => setExpanded(true)}
      />
    );
  }
  return (
    <SignupForm
      copy={copy}
      inputId={inputId}
      email={email}
      onEmailChange={setEmail}
      onSubmit={() => signup.submit(email.trim())}
      isPending={signup.isPending}
      error={signup.error}
      className={className}
    />
  );
}

function SignupHeader({ copy, inputId }: { copy: AlertCopy; inputId: string }) {
  const Icon = copy.icon;
  return (
    <div className="flex items-start gap-2">
      <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
      <div>
        <label
          htmlFor={inputId}
          className="text-sm font-medium text-foreground"
        >
          {copy.label}
        </label>
        {copy.description ? (
          <p className="text-sm text-muted-foreground">{copy.description}</p>
        ) : null}
      </div>
    </div>
  );
}

function SignupForm({
  copy,
  inputId,
  email,
  onEmailChange,
  onSubmit,
  isPending,
  error,
  className,
}: {
  copy: AlertCopy;
  inputId: string;
  email: string;
  onEmailChange: (email: string) => void;
  onSubmit: () => void;
  isPending: boolean;
  error: string | null;
  className?: string;
}) {
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (email.trim()) onSubmit();
      }}
      className={cn(
        "flex flex-col gap-3 rounded-md border border-border bg-card px-4 py-4",
        className,
      )}
    >
      <SignupHeader copy={copy} inputId={inputId} />
      <EmailRow
        id={inputId}
        email={email}
        onChange={onEmailChange}
        isPending={isPending}
        buttonLabel={copy.buttonLabel}
      />
      {error ? (
        <p role="alert" className="text-xs text-destructive-strong">
          {error}
        </p>
      ) : null}
    </form>
  );
}
